/**
 * EKSTRE BİÇİMİ MASKESİ (kullanıcı kararı 21.10.2026).
 *
 * Kurum ekstresi okuyucusu yazılabilmesi için ekstrenin DÜZENİ gerekir,
 * içeriği değil. Kullanıcı PDF'ini kendi cihazında bu maskeden geçirir ve
 * çıkan dosyayı kendisi paylaşır; gerçek ekstre cihazdan çıkmaz.
 *
 * Kurallar (tek kopya; ekran ve dosya aynı maskeyi kullanır):
 *   - her rakam `9` olur (biçim görünür, değer gitmez: 12.345,67 → 99.999,99);
 *   - harf dizisi (kelime) sözlükte ya da kullanıcının açtıklarındaysa
 *     açık kalır; değilse her harfi büyükse `X`, küçükse `x` olur;
 *   - kullanıcının kapattığı kelime sözlükte olsa da kapanır;
 *   - noktalama, boşluk, para işaretleri olduğu gibi kalır.
 * Dosyaya yalnızca maskeli metin ve konumu girer: dosya adı, PDF'in iç
 * bilgileri (yazar, başlık), görseller ve yazı tipi adları GİRMEZ.
 *
 * Bilinçli sınır: kelimenin uzunluğu görünür (Xxxxx Xxxxxx).
 */

import { CURRENCIES } from "@parafon/contracts";

/** PDF'ten çıkan bir metin parçası. Konum sayfanın sol üstünden, punto biriminde. */
export interface MetinParcasi {
  readonly x: number;
  /** Taban çizgisinin sayfanın üstünden uzaklığı. */
  readonly y: number;
  readonly genislik: number;
  /** Yazı boyu (punto). */
  readonly boy: number;
  readonly metin: string;
}

export interface EkstreSayfasi {
  readonly no: number;
  readonly genislik: number;
  readonly yukseklik: number;
  readonly parcalar: readonly MetinParcasi[];
}

/** Kullanıcının kelime seçimleri; anahtar `kelimeAnahtari` ile. */
export interface MaskeSecimi {
  readonly acilan: ReadonlySet<string>;
  readonly kapatilan: ReadonlySet<string>;
}

export const BOS_SECIM: MaskeSecimi = { acilan: new Set(), kapatilan: new Set() };

/**
 * Açık kalan başlık kelimeleri (küçük harf). Kişi adı olarak da kullanılan
 * kelimeler (faiz, altın, gümüş, ay, gün, can, deniz …) BİLEREK yok:
 * gerekirse kullanıcı açar.
 */
const SOZLUK_TR = `
hesap hesabı hesabın hesaplar ekstre ekstresi döküm dökümü özet özeti rapor raporu
tarih tarihi tarihli işlem işlemi işlemler işlemleri işlemin tür türü tip tipi çeşit
açıklama açıklaması detay detayı ayrıntı
alış alım alımı satış satım satışı alınan satılan
adet adedi miktar miktarı nominal lot pay payı paylar
fiyat fiyatı fiyatlar birim ortalama ort maliyet maliyeti
tutar tutarı tutarlar toplam toplamı ara genel net brüt
komisyon komisyonu masraf masrafı masraflar ücret ücreti ücretler gider gideri
vergi vergisi bsmv kdv stopaj stopajı kesinti kesintisi
valör valörü takas emir emri emirler gerçekleşen gerçekleşme referans
no numara numarası numaralı sıra sayı
hisse hissesi hisseler senet senedi menkul kıymet kıymetler kıymeti enstrüman
varlık varlıklar varlığı sembol sembolü kod kodu ad adı unvan unvanı isim
fon fonu fonlar döviz kur kuru kurlar para paranın birimi cinsi
yatırma yatırılan çekme çekilen transfer virman havale eft swift
temettü temettüsü kâr kar zarar kazanç kazancı getiri getirisi
bedelsiz bedelli sermaye artırımı bölünme rüçhan hakkı
nakit nakdi bakiye bakiyesi bakiyeler devir devreden devirli bloke kullanılabilir
giriş girişi çıkış çıkışı borç borcu alacak alacağı
dönem dönemi dönemler başlangıç bitiş itibarıyla itibariyle
sayfa portföy portföyü değer değeri değerleme piyasa güncel son ilk
oran oranı yüzde yıl yılı saat
borsa borsası bist nasdaq nyse amex otc abd yurt dışı içi yerli yabancı
müşteri müşterisi kimlik vergi adres adresi telefon posta e
yatırım yatırımcı aracı kurum kurumu şube şubesi
ve ile için veya olan olarak göre den dan de da ki bu şu
`;

const SOZLUK_EN = `
account statement summary report details detail activity activities history
date dates trade trades transaction transactions type description settlement settled
buy bought sell sold purchase sale
quantity qty shares share units unit price prices average avg cost basis
amount amounts total totals subtotal net gross
commission commissions fee fees charge charges tax taxes withholding withheld
dividend dividends reinvest reinvested distribution
deposit deposits withdrawal withdrawals transfer transfers wire incoming outgoing
cash balance balances opening closing beginning ending starting period from to
symbol ticker security securities instrument name currency fx rate exchange
order orders executed execution side status filled reference ref id no number
market value values realized unrealized gain gains loss losses profit return
portfolio position positions holding holdings stock stocks equity equities etf etfs fund funds
customer address page of and or the in on at by for per as
`;

function kelimeListesi(metin: string): readonly string[] {
  return metin.split(/\s+/u).filter((k) => k !== "");
}

const SOZLUK: ReadonlySet<string> = new Set([
  ...kelimeListesi(SOZLUK_TR),
  ...kelimeListesi(SOZLUK_EN),
  ...CURRENCIES.map((c) => c.code.toLowerCase()),
  "tl",
]);

const KELIME = /[\p{L}\p{M}]+/gu;
const RAKAM = /\p{Nd}/gu;

/**
 * Kelimenin anahtarı: Türkçe küçük harf. "İŞLEM" → "işlem". İngilizce
 * büyük harf "TICKER" Türkçede "tıcker" olur; sözlük ikisine de bakar.
 */
export function kelimeAnahtari(kelime: string): string {
  return kelime.toLocaleLowerCase("tr");
}

function sozlukte(kelime: string): boolean {
  return SOZLUK.has(kelimeAnahtari(kelime)) || SOZLUK.has(kelime.toLowerCase());
}

/** Kelime maskeden sonra açık mı? Kullanıcının seçimi sözlükten önce gelir. */
export function kelimeAcikMi(kelime: string, secim: MaskeSecimi): boolean {
  const k = kelimeAnahtari(kelime);
  if (secim.kapatilan.has(k)) return false;
  if (secim.acilan.has(k)) return true;
  return sozlukte(kelime);
}

/** Sözlüğe göre varsayılan (kullanıcı seçimi olmadan) açık mı? */
export function varsayilanAcikMi(kelime: string): boolean {
  return sozlukte(kelime);
}

function harfMaskesi(kelime: string): string {
  let out = "";
  for (const h of kelime) {
    if (/\p{M}/u.test(h)) continue;
    out += h !== h.toLocaleLowerCase("tr") ? "X" : "x";
  }
  return out;
}

export function maskele(metin: string, secim: MaskeSecimi): string {
  return metin
    .replace(RAKAM, "9")
    .replace(KELIME, (k) => (kelimeAcikMi(k, secim) ? k : harfMaskesi(k)));
}

export interface KelimeSayimi {
  readonly anahtar: string;
  /** Ekranda gösterilen hâli (ilk görüldüğü yazılış). Yalnızca cihazda. */
  readonly yazilis: string;
  readonly adet: number;
  readonly varsayilanAcik: boolean;
}

/** Belgedeki bütün kelimeler, en sık geçen önce (eşitlikte alfabetik). */
export function kelimeleriSay(sayfalar: readonly EkstreSayfasi[]): readonly KelimeSayimi[] {
  const m = new Map<string, { yazilis: string; adet: number; varsayilanAcik: boolean }>();
  for (const s of sayfalar)
    for (const p of s.parcalar)
      for (const k of p.metin.match(KELIME) ?? []) {
        const a = kelimeAnahtari(k);
        const v = m.get(a);
        if (v === undefined) m.set(a, { yazilis: k, adet: 1, varsayilanAcik: sozlukte(k) });
        else v.adet += 1;
      }
  return [...m.entries()]
    .map(([anahtar, v]) => ({ anahtar, ...v }))
    .sort((a, b) => b.adet - a.adet || a.anahtar.localeCompare(b.anahtar, "tr"));
}

/** Aynı satırda sayılan dikey fark: yazı boyunun yarısı. */
function ayniSatir(a: MetinParcasi, b: MetinParcasi): boolean {
  return Math.abs(a.y - b.y) <= Math.max(a.boy, b.boy, 1) * 0.5;
}

/** Bir sütun = 4 punto; okunabilir bir düz metin görünümü için. */
const SUTUN = 4;

/**
 * Sayfanın düz metin görünümü: parçalar satırlara toplanır, soldan sağa,
 * yatay konuma göre boşlukla hizalanır. Ekrandaki önizleme ve dosyadaki
 * `gorunum` aynı işlevden.
 */
export function sayfaGorunumu(parcalar: readonly MetinParcasi[], secim: MaskeSecimi): readonly string[] {
  const sirali = [...parcalar].sort((a, b) => a.y - b.y || a.x - b.x);
  const satirlar: MetinParcasi[][] = [];
  for (const p of sirali) {
    const son = satirlar[satirlar.length - 1];
    if (son !== undefined && ayniSatir(son[0]!, p)) son.push(p);
    else satirlar.push([p]);
  }
  return satirlar.map((satir) => {
    let out = "";
    for (const p of [...satir].sort((a, b) => a.x - b.x)) {
      const sutun = Math.round(p.x / SUTUN);
      if (out.length > 0 && out.length >= sutun) out += " ";
      else out = out.padEnd(sutun, " ");
      out += maskele(p.metin, secim);
    }
    return out.trimEnd();
  });
}

export const MASKE_BICIMI = "parafon-ekstre-bicimi";
export const MASKE_SURUMU = 1;

const yuvarla = (n: number): number => Math.round(n * 10) / 10;

/**
 * Paylaşılacak dosyanın içeriği (JSON metni). Okuyucuyu yazmak için
 * gereken her şey: sayfa boyutu, her parçanın konumu, boyu ve maskeli metni.
 */
export function maskeDosyasi(sayfalar: readonly EkstreSayfasi[], secim: MaskeSecimi): string {
  const govde = {
    bicim: MASKE_BICIMI,
    surum: MASKE_SURUMU,
    kural: "rakam → 9; sözlükte olmayan harf → X/x; konum punto, sayfanın sol üstünden",
    sayfalar: sayfalar.map((s) => ({
      no: s.no,
      genislik: yuvarla(s.genislik),
      yukseklik: yuvarla(s.yukseklik),
      gorunum: sayfaGorunumu(s.parcalar, secim),
      parcalar: s.parcalar.map((p) => [yuvarla(p.x), yuvarla(p.y), yuvarla(p.genislik), yuvarla(p.boy), maskele(p.metin, secim)]),
    })),
  };
  return `${JSON.stringify(govde, null, 1)}\n`;
}
